import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2 } from 'lucide-react';

export const HealthDialog = ({
  open,
  onOpenChange,
  selectedSheep,
  healthFormData,
  setHealthFormData,
  onSubmit,
  healthRecords,
  canWrite,
  canDelete,
  onDeleteRecord,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Carnet de sante - {selectedSheep?.nom || `N ${selectedSheep?.numero}`}</DialogTitle>
        <DialogDescription>Gerez les vaccins, traitements et controles veterinaires</DialogDescription>
      </DialogHeader>

      {canWrite && (
        <form onSubmit={onSubmit} className="space-y-4 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-medium text-gray-900 dark:text-white">Ajouter un enregistrement</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="health-type">Type *</Label>
              <Select value={healthFormData.type} onValueChange={(value) => setHealthFormData({ ...healthFormData, type: value })}>
                <SelectTrigger data-testid="health-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vaccin">Vaccin</SelectItem>
                  <SelectItem value="traitement">Traitement</SelectItem>
                  <SelectItem value="controle">Controle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-nom">Nom *</Label>
              <Input
                id="health-nom"
                value={healthFormData.nom}
                onChange={(e) => setHealthFormData({ ...healthFormData, nom: e.target.value })}
                required
                data-testid="health-nom-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="health-date">Date *</Label>
              <Input
                id="health-date"
                type="date"
                value={healthFormData.date_administration}
                onChange={(e) => setHealthFormData({ ...healthFormData, date_administration: e.target.value })}
                required
                data-testid="health-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-prochaine">Prochaine date</Label>
              <Input
                id="health-prochaine"
                type="date"
                value={healthFormData.prochaine_date}
                onChange={(e) => setHealthFormData({ ...healthFormData, prochaine_date: e.target.value })}
                data-testid="health-prochaine-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="health-veterinaire">Veterinaire</Label>
              <Input
                id="health-veterinaire"
                value={healthFormData.veterinaire}
                onChange={(e) => setHealthFormData({ ...healthFormData, veterinaire: e.target.value })}
                data-testid="health-veterinaire-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-notes">Notes</Label>
              <Input
                id="health-notes"
                value={healthFormData.notes}
                onChange={(e) => setHealthFormData({ ...healthFormData, notes: e.target.value })}
                data-testid="health-notes-input"
              />
            </div>
          </div>
          <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700" data-testid="health-submit-button">
            Ajouter l'enregistrement
          </Button>
        </form>
      )}

      <div className="space-y-3">
        <h3 className="font-medium text-gray-900 dark:text-white">Historique</h3>
        {healthRecords.length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucun enregistrement</p>
        ) : (
          healthRecords.map((record) => (
            <div key={record.id} className="p-4 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    record.type === 'vaccin' ? 'bg-blue-100 text-blue-700' :
                    record.type === 'traitement' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {record.type === 'vaccin' ? 'Vaccin' : record.type === 'traitement' ? 'Traitement' : 'Controle'} - {record.type}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">{record.nom}</span>
                </div>
                {canDelete && (
                  <Button variant="ghost" size="sm" onClick={() => onDeleteRecord(record.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="font-medium">Date:</span> {new Date(record.date).toLocaleDateString('fr-FR')}</p>
                {record.prochaine_date && (
                  <p><span className="font-medium">Prochaine date:</span> {new Date(record.prochaine_date).toLocaleDateString('fr-FR')}</p>
                )}
                {record.veterinaire && <p><span className="font-medium">Veterinaire:</span> {record.veterinaire}</p>}
                {record.notes && <p><span className="font-medium">Notes:</span> {record.notes}</p>}
              </div>
            </div>
          ))
        )}
      </div>
    </DialogContent>
  </Dialog>
);
